import type { paths } from "../types/api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type APIDirectory =
  paths["/api/v1/directories/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["directory"];

// UI type - converts numeric id to string for HierarchicalItem compatibility
export type Directory = Omit<APIDirectory, "id"> & {
  id: string;
  [key: string]: unknown;
};
export type DirectoryTreeNode = Directory & { children: DirectoryTreeNode[] };

// Constants
const DEFAULT_TREE_DEPTH = 4;

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type DirectoryListResponse =
  paths["/api/v1/directories"]["get"]["responses"]["200"]["content"]["application/json"];
export type DirectorySingleResponse =
  paths["/api/v1/directories/{id}"]["get"]["responses"]["200"]["content"]["application/json"];
export type DirectoryAncestorsResponse =
  paths["/api/v1/directories/{id}/ancestors"]["get"]["responses"]["200"]["content"]["application/json"];
export type DirectoryChildrenResponse =
  paths["/api/v1/directories/{id}/children"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Service
// ============================================================================

// Map API directory to UI type (converts numeric id to string)
function mapToDirectory(apiDirectory: APIDirectory): Directory {
  return {
    ...apiDirectory,
    id: String(apiDirectory.id),
  };
}

// The OpenAPI generated type only shows children one level deep, but runtime data is recursive
type DirectoryWithRecursiveChildren = DirectoryChildrenResponse["directories"][0] & {
  children?: DirectoryWithRecursiveChildren[];
};

function mapDirectoryTree(d: DirectoryWithRecursiveChildren): DirectoryTreeNode {
  return {
    ...d,
    id: String(d.id),
    children: d.children?.map(mapDirectoryTree) || [],
  };
}

export const directories = {
  getAll: async (): Promise<Directory[]> => {
    const response = await api.get<DirectoryListResponse>("/v1/taxonomy/directories");
    return response.directories.map(mapToDirectory);
  },

  getById: async (id: string): Promise<Directory> => {
    const response = await api.get<DirectorySingleResponse>(`/v1/taxonomy/directories/${id}`);
    return mapToDirectory(response.directory);
  },

  getAncestors: async (id: string): Promise<Directory[]> => {
    const response = await api.get<DirectoryAncestorsResponse>(
      `/v1/taxonomy/directories/${id}/ancestors`,
    );
    return response.ancestors.map(mapToDirectory);
  },

  getChildren: async (
    id: string,
    depth: number = DEFAULT_TREE_DEPTH,
  ): Promise<DirectoryTreeNode[]> => {
    const response = await api.get<DirectoryChildrenResponse>(
      `/v1/taxonomy/directories/${id}/children?depth=${depth}`,
    );
    return (response.directories as DirectoryWithRecursiveChildren[]).map(mapDirectoryTree);
  },

  create: async (data: {
    name: string;
    code: string;
    parentId?: string | null;
  }): Promise<Directory> => {
    const response = await api.post<DirectorySingleResponse>("/v1/taxonomy/directories", data);
    return mapToDirectory(response.directory);
  },

  update: async (
    id: string,
    data: {
      name?: string;
      code?: string;
      displayOrder?: number;
    },
  ): Promise<Directory> => {
    const response = await api.put<DirectorySingleResponse>(`/v1/taxonomy/directories/${id}`, data);
    return mapToDirectory(response.directory);
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/taxonomy/directories/${id}`);
  },

  move: async (id: string, parentId: string | null): Promise<Directory> => {
    const response = await api.put<DirectorySingleResponse>(`/v1/taxonomy/directories/${id}/move`, {
      parentId,
    });
    return mapToDirectory(response.directory);
  },
};
